import { HttpContext, HttpErrorResponse } from '@angular/common/http';
import { computed, effect, inject, Injectable, signal } from '@angular/core';
import {
  MedicalAlertDefinitionV2,
  MedicalAlertsDataService,
  PatientContactsDataService,
  PatientContactTypeEnum,
  PatientDataService,
  PatientDetailsV2,
  PatientNoteV2,
  PatientNotesDataService,
  ProvisionalStatus,
  UpdatePatientContactRequest,
} from '@ffd/api-client';
import { ToastService } from '@libs/shared-services';
import {
  healthHistoryActions,
  patientActions,
  patientAlertsActions,
  selectHealthHistoryCount,
  selectIsEaglesoftPms,
  selectPatientAlertsCount,
  selectProvisionalLocksEditing,
  selectSelectedPatient,
  selectUserSite,
} from '@libs/store';
import { ConfirmDialogComponent, ConfirmDialogResult, DropdownItem } from '@libs/ui-lib';

import {
  DataState,
  DISABLE_GLOBAL_ERROR_TOASTS,
  FfdComponentStore,
  isLockedByProvisional,
  Loadable,
  routes,
  SiteFeatureFlag,
} from '@libs/utils';
import { concatLatestFrom, tapResponse } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { distinctUntilChanged, filter, forkJoin, Observable, of, switchMap, tap } from 'rxjs';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { ProfileContact } from './components/health-history-patient-details/components/health-history-patient-details-secondary-contacts/secondary-contact-validators';

interface HealthHistoryState {
  siteId: string;
  patientId: string;
  patientNotes: Loadable<PatientNoteV2[]>;
  medicalAlertsDefinitions: Loadable<MedicalAlertDefinitionV2[]>;
  hasDirtyForm: boolean;
  patient?: PatientDetailsV2;
}

export interface SecondaryContactVM {
  contactType: PatientContactTypeEnum;
  contactName: string;
  phoneNumber: string;
  // VM-only fields — NOT in the API yet. Always '' for now.
  relationshipToPatient?: string; // EMERGENCY_CONTACT only
  speciality?: string; // PHYSICIAN_CONTACT only
  fax?: string; // PREFERRED_PHARMACY only
  address?: string; // PREFERRED_PHARMACY only
}

export interface HealthHistoryVM {
  patient?: PatientDetailsV2;
  patientNotes: string;
  patientNotesLoading: boolean;
  medicalAlertDefinitions: DropdownItem<string>[];
  hasDirtyForm: boolean;
  secondaryContacts: SecondaryContactVM[];
  anyContactProvisional: boolean;
  anyContactLockedByProvisional: boolean;
}

@Injectable({ providedIn: 'root' })
export class HealthHistoryDialogService extends FfdComponentStore<HealthHistoryState, HealthHistoryVM> {
  private readonly medicalAlertsDataService = inject(MedicalAlertsDataService);
  private readonly patientDataService = inject(PatientDataService);
  private readonly patientNotesDataService = inject(PatientNotesDataService);
  private readonly patientContactsDataService = inject(PatientContactsDataService);
  private readonly store = inject(Store);
  private readonly provisionalLocksEditing = this.store.selectSignal(selectProvisionalLocksEditing);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly dialog = inject(Dialog);

  // The patient for which the initial data is fetched in showcase
  private readonly fetchedShowcasePatient = signal<string | null>(null);
  private readonly userSite = this.store.selectSignal(selectUserSite);
  private readonly isEaglesoft = this.store.selectSignal(selectIsEaglesoftPms);

  public healthHistoryCount = this.store.selectSignal(selectHealthHistoryCount);
  public patientAlertsCount = this.store.selectSignal(selectPatientAlertsCount);

  public siteIdSignal = this.selectSignal((state) => state.siteId);
  public patientIdSignal = this.selectSignal((state) => state.patientId);
  public medicalAlertDefinitions = this.selectSignal((state) => state.medicalAlertsDefinitions.data);

  // Feature flag signals for health history
  public readonly addHealthHistoryEnabled = computed(
    () => this.userSite()?.featureFlags?.find(({ featureFlag }) => featureFlag === SiteFeatureFlag.AddHealthHistory)?.isEnabled || false,
  );
  public readonly editHealthHistoryEnabled = computed(
    () => this.userSite()?.featureFlags?.find(({ featureFlag }) => featureFlag === SiteFeatureFlag.EditHealthHistory)?.isEnabled || false,
  );
  public readonly deleteHealthHistoryEnabled = computed(
    () => this.userSite()?.featureFlags?.find(({ featureFlag }) => featureFlag === SiteFeatureFlag.DeleteHealthHistory)?.isEnabled || false,
  );

  // Feature flag signals for patient alerts
  public readonly addPatientAlertEnabled = computed(
    () => this.userSite()?.featureFlags?.find(({ featureFlag }) => featureFlag === SiteFeatureFlag.AddPatientAlert)?.isEnabled || false,
  );
  public readonly editPatientAlertEnabled = computed(
    () => this.userSite()?.featureFlags?.find(({ featureFlag }) => featureFlag === SiteFeatureFlag.EditPatientAlert)?.isEnabled || false,
  );
  public readonly deletePatientAlertEnabled = computed(
    () => this.userSite()?.featureFlags?.find(({ featureFlag }) => featureFlag === SiteFeatureFlag.DeletePatientAlert)?.isEnabled || false,
  );

  constructor() {
    super({
      siteId: '',
      patientId: '',
      hasDirtyForm: false,
      patientNotes: {
        data: [],
        state: DataState.Initial,
      },
      medicalAlertsDefinitions: {
        data: [],
        state: DataState.Initial,
      },
    });

    effect(() => {
      const patient = this.selectSignal((state) => state.patient)();
      const selectedPatient = this.store.selectSignal(selectSelectedPatient)();

      if (selectedPatient.data && selectedPatient.data.id !== patient?.id) {
        this.patchState({
          patient: selectedPatient.data,
        });
      }
    });
  }

  protected setMedicalAlertDefinitions = this.updater(
    (state, data: { medicalAlerts: MedicalAlertDefinitionV2[]; state: DataState }): HealthHistoryState => {
      return {
        ...state,
        medicalAlertsDefinitions: {
          data: data.medicalAlerts,
          state: data.state,
        },
      };
    },
  );

  public readonly setSiteId = this.updater((state, siteId: string): HealthHistoryState => {
    return {
      ...state,
      siteId,
    };
  });

  public readonly setPatientId = this.updater((state, patientId: string): HealthHistoryState => {
    return {
      ...state,
      patientId,
    };
  });

  public setFormDirtyState = this.updater((state, hasDirtyForm: boolean): HealthHistoryState => {
    return {
      ...state,
      hasDirtyForm,
    };
  });

  public readonly setPatientNotes = this.updater((state, patientNotes: PatientNoteV2[]): HealthHistoryState => {
    return {
      ...state,
      patientNotes: {
        data: patientNotes,
        state: DataState.Fetched,
      },
    };
  });

  public override init(): void {
    this.loadPatient(); // Trigger the patient loading effect
    this.loadMedicalAlertDefinitions();
    this.getPatientNotes();
    this.loadPatientAlerts();
  }

  protected override mapStateToViewModel(state: HealthHistoryState): HealthHistoryVM {
    const patient = state.patient;
    const contacts = patient?.contacts ?? [];
    const anyContactProvisional = contacts.some((c) => (c.provisionalStatus ?? ProvisionalStatus.READY) !== ProvisionalStatus.READY);
    const anyContactLockedByProvisional = contacts.some((c) => isLockedByProvisional(c.provisionalStatus, this.provisionalLocksEditing()));

    return {
      patient,
      patientNotes: (Array.isArray(state.patientNotes.data) ? state.patientNotes.data : [])
        .filter((el) => !!el)
        .map((note) => note.text)
        .join('\n'),
      patientNotesLoading: state.patientNotes.state === DataState.Loading,
      medicalAlertDefinitions: (state.medicalAlertsDefinitions?.data ?? []).map((item) => ({
        label: item.description ?? '',
        value: item.id,
      })),
      hasDirtyForm: state.hasDirtyForm,
      secondaryContacts: this.buildSecondaryContacts(patient),
      anyContactProvisional,
      anyContactLockedByProvisional,
    };
  }

  private buildSecondaryContacts(patient: PatientDetailsV2 | undefined): SecondaryContactVM[] {
    const findContact = (type: PatientContactTypeEnum) => (patient?.contacts ?? []).find((c) => c.contactType === type);

    const toVm = (type: PatientContactTypeEnum, extra: Partial<SecondaryContactVM>): SecondaryContactVM => {
      const c = findContact(type);
      return {
        contactType: type,
        contactName: c?.contactName ?? '',
        phoneNumber: c?.phoneNumber ?? '',
        ...extra,
      };
    };

    return [
      toVm(PatientContactTypeEnum.EMERGENCY_CONTACT, { relationshipToPatient: '' }),
      toVm(PatientContactTypeEnum.PREFERRED_PHARMACY, { fax: '', address: '' }),
      toVm(PatientContactTypeEnum.PHYSICIAN_CONTACT, { speciality: '' }),
    ];
  }

  private readonly updatePatientContacts = this.updater((state, patient: UpdatePatientContactRequest): HealthHistoryState => {
    const updatedContacts = state.patient?.contacts?.map((contact) => contact.contactType).includes(patient.contactType)
      ? state.patient?.contacts?.map((contact) => {
          if (contact.contactType === patient.contactType) {
            return {
              ...contact,
              contactName: patient.contactName,
              phoneNumber: patient.phoneNumber,
            };
          }
          return contact;
        })
      : [
          ...(state.patient?.contacts ?? []),
          {
            contactType: patient.contactType,
            contactName: patient.contactName,
            phoneNumber: patient.phoneNumber,
          },
        ];

    return {
      ...state,
      patient: state.patient ? { ...state.patient, contacts: updatedContacts } : state.patient,
    };
  });

  private readonly loadMedicalAlertDefinitions = this.effect(($) =>
    $.pipe(
      concatLatestFrom(() => [this.select((state) => state.siteId)]),
      distinctUntilChanged(),
      switchMap(([_, siteId]) => {
        this.setMedicalAlertDefinitions({ state: DataState.Loading, medicalAlerts: [] });
        return this.medicalAlertsDataService.getMedicalAlertDefinitionsBySiteV2(siteId).pipe(
          tapResponse(
            (medicalAlerts) => {
              this.setMedicalAlertDefinitions({ medicalAlerts, state: DataState.Fetched });
            },
            (error: HttpErrorResponse) => {
              if (!this.isEaglesoft()) {
                this.toastService.error(this.translateService.instant('common_error_message'));
              }

              console.error(error);
              this.setMedicalAlertDefinitions({
                medicalAlerts: [],
                state: DataState.Error,
              });
            },
          ),
        );
      }),
    ),
  );

  public readonly getPatientNotes = this.effect(($) =>
    $.pipe(
      concatLatestFrom(() => [this.select((state) => state.siteId), this.select((state) => state.patientId)]),
      filter((args): args is [void, string, string] => !!args[1] && !!args[2]),
      tap(([_, siteId, patientId]) => {
        // for the fetched showcase patient, we cannot change the state to loading, because we won't be able to fetch the notes
        if (siteId === routes.showcaseSiteId && this.fetchedShowcasePatient() === patientId) {
          return;
        }

        this.patchState({
          patientNotes: {
            data: this.state().patientNotes.data,
            state: DataState.Loading,
          },
        });
      }),
      switchMap(([_, siteId, patientId]) => {
        // for the fetched showcase patient, the notes cannot be fetched, because it would reset the users changes
        // in showcase, since the showcase doesn't send the changes to the backend
        if (siteId === routes.showcaseSiteId && this.fetchedShowcasePatient() === patientId) {
          return of();
        }

        return this.patientNotesDataService
          .getPatientNotesV2(siteId, patientId, undefined, undefined, {
            context: new HttpContext().set(DISABLE_GLOBAL_ERROR_TOASTS, true),
          })
          .pipe(
            tapResponse(
              (notes) => {
                // the fetched showcase patient is stored, so it won't be overwritten by fetching new data
                if (siteId === routes.showcaseSiteId) {
                  this.fetchedShowcasePatient.set(patientId);
                }
                this.setPatientNotes(notes);
              },
              (error: HttpErrorResponse) => {
                if (!this.isEaglesoft()) {
                  this.toastService.error(this.translateService.instant('error_message_server_error_patient_by_site'));
                }

                console.error(error);
                this.setPatientNotes([]);
              },
            ),
          );
      }),
    ),
  );

  public readonly loadPatient = this.effect(($: Observable<{ forceReload?: boolean } | void>) => {
    const selectedPatient = this.store.selectSignal(selectSelectedPatient)();
    if (selectedPatient.data) {
      this.patchState({
        patient: selectedPatient.data,
      });
    }

    return $.pipe(
      filter((params) => {
        if (params?.forceReload ?? false) {
          return true;
        }
        const selectedPatient = this.store.selectSignal(selectSelectedPatient)();
        return !selectedPatient.data;
      }),
      concatLatestFrom(() => [this.select((state) => state.siteId), this.select((state) => state.patientId)]),
      filter(([, siteId, patientId]) => !!siteId && !!patientId),
      switchMap(([, siteId, patientId]) => {
        // for the fetched showcase patient, the patient cannot be fetched, because it would reset the users changes
        // in showcase, since the showcase doesn't send the changes to the backend
        if (siteId === routes.showcaseSiteId && this.fetchedShowcasePatient() === patientId) {
          return of();
        }

        return this.patientDataService.getPatientBySiteV2(siteId, patientId).pipe(
          tapResponse(
            (patient) => {
              // the fetched showcase patient is stored, so it won't be overwritten by fetching new data
              if (siteId === routes.showcaseSiteId) {
                this.fetchedShowcasePatient.set(patientId);
              }
              this.patchState({ patient });
            },
            (error) => {
              console.error(error);

              if (!this.isEaglesoft()) {
                this.toastService.error(this.translateService.instant('common_error_message'));
              }
            },
          ),
        );
      }),
    );
  });

  public readonly updatePatientContact = this.effect((contactUpdate$: Observable<ProfileContact>) =>
    contactUpdate$.pipe(
      concatLatestFrom(() => [this.select((state) => state.siteId), this.select((state) => state.patientId)]),
      filter(([, siteId, patientId]) => !!siteId && !!patientId),
      switchMap(([contactUpdate, siteId, patientId]) => {
        const updateRequest = this.buildUpdatePatientContactRequest(contactUpdate);

        return this.patientContactsDataService.updatePatientContact(siteId, patientId, updateRequest).pipe(
          tapResponse(
            () => {
              this.store.dispatch(
                patientActions.onLoadPatient({
                  patientId,
                  silentReload: true,
                }),
              );
            },
            (error: HttpErrorResponse) => {
              console.error('Error updating patient contact:', error);
              this.toastService.error(this.translateService.instant('common_error_message'));
            },
          ),
        );
      }),
    ),
  );

  public readonly updateMultiplePatientContacts = this.effect((contactUpdates$: Observable<Array<ProfileContact>>) =>
    contactUpdates$.pipe(
      concatLatestFrom(() => [this.select((state) => state.siteId), this.select((state) => state.patientId)]),
      filter(([, siteId, patientId]) => !!siteId && !!patientId),
      switchMap(([contactUpdates, siteId, patientId]) => {
        // Create parallel requests for all contacts
        const updateRequests = contactUpdates.map((contactUpdate) => {
          const updateRequest = this.buildUpdatePatientContactRequest(contactUpdate);
          if (siteId === routes.showcaseSiteId) {
            return of(this.updatePatientContacts(updateRequest));
          }
          return this.patientContactsDataService.updatePatientContact(siteId, patientId, updateRequest);
        });

        return forkJoin(updateRequests).pipe(
          tapResponse(
            () => {
              if (siteId === routes.showcaseSiteId) {
                return;
              }
              this.loadPatient({ forceReload: true });
            },
            (error: HttpErrorResponse) => {
              console.error('Error updating patient contacts:', error);
              this.loadPatient({ forceReload: true });
              this.toastService.error(this.translateService.instant('common_error_message'));
            },
          ),
        );
      }),
    ),
  );

  public resetHealthHistory(): void {
    this.store.dispatch(healthHistoryActions.resetHealthHistory());
  }

  public openCancelConfirmationDialog(): DialogRef<ConfirmDialogResult> {
    const dialogRef = this.dialog.open<ConfirmDialogResult>(ConfirmDialogComponent, {
      backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
      panelClass: ['w-[480px]'],
      disableClose: true,
      data: {
        title: 'health_history_health_history_tab_item_edit_cancel_modal_title',
        description: 'health_history_health_history_tab_item_edit_cancel_modal_description',
        cancelLabel: 'health_history_health_history_tab_item_edit_cancel_modal_continue',
        confirmLabel: 'health_history_health_history_tab_item_edit_cancel_modal_discard',
      },
    });

    dialogRef.closed.subscribe((result) => {
      if (result?.confirm) {
        this.setFormDirtyState(false);
      }
    });

    return dialogRef;
  }

  private loadPatientAlerts(): void {
    this.store.dispatch(patientAlertsActions.getPatientAlerts({ siteId: this.state().siteId, patientId: this.state().patientId }));
  }

  private readonly buildUpdatePatientContactRequest = (contactUpdate: ProfileContact): UpdatePatientContactRequest => {
    return {
      contactType: contactUpdate.contactType,
      contactName: contactUpdate.contactName || '',
      phoneNumber: contactUpdate.phoneNumber || '',
    };
  };
}
